/**
 * Parse location data from various formats
 * @param {string} location - The location string from API
 * @returns {Object|null} - Parsed location with lat, lng or null if invalid
 */
export const parseLocation = (location) => {
  if (!location || location === 'N/A' || location === 'null') {
    return null;
  }

  // Try parsing as JSON first
  try {
    const parsed = JSON.parse(location);

    // Check if it has latitude and longitude
    if (parsed.latitude && parsed.longitude) {
      return {
        lat: parseFloat(parsed.latitude),
        lng: parseFloat(parsed.longitude),
        ip: parsed.ip,
        type: 'json'
      };
    }
  } catch (e) {
    // Not JSON, continue to other formats
  }

  // Try parsing as comma-separated coordinates
  if (location.includes(',')) {
    const parts = location.split(',').map(part => part.trim());

    if (parts.length === 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);

      // Validate coordinates
      if (!isNaN(lat) && !isNaN(lng) &&
          lat >= -90 && lat <= 90 &&
          lng >= -180 && lng <= 180) {
        return {
          lat,
          lng,
          type: 'coordinates'
        };
      }
    }
  }

  // If it's a plain text location name, return it as is
  // This can be used for geocoding later if needed
  return {
    name: location,
    type: 'text',
    lat: null,
    lng: null
  };
};

/**
 * Get all valid locations from trades array
 * @param {Array} trades - Array of trade objects
 * @returns {Array} - Array of trades with valid parsed locations
 */
export const getValidLocations = (trades) => {
  return trades
    .map(trade => {
      const parsedLocation = parseLocation(trade.location);

      // Only include if we have valid coordinates
      if (parsedLocation && parsedLocation.lat && parsedLocation.lng) {
        return {
          ...trade,
          parsedLocation
        };
      }
      return null;
    })
    .filter(Boolean);
};

/**
 * Get marker color based on stake status
 * @param {string} status - The stake status
 * @returns {string} - Hex color for the marker
 */
export const getMarkerColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'active':
      return '#11998e';  // Green
    case 'pending':
      return '#FFA726';  // Orange
    case 'completed':
      return '#2196F3';  // Blue
    case 'inactive':
      return '#f44336';  // Red
    default:
      return '#6c757d';  // Gray
  }
};

/**
 * Format location display text
 * @param {Object} location - Parsed location object
 * @returns {string} - Formatted location text
 */
export const formatLocationText = (location) => {
  if (!location) return 'N/A';

  if (location.type === 'text') {
    return location.name;
  }

  if (location.lat && location.lng) {
    return `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`;
  }

  return 'N/A';
};